import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext();

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export const AuthProvider = ({ children }) => {
  const [user, setUser]               = useState(null);
  const [loading, setLoading]         = useState(true);
  const [subscription, setSubscription] = useState(null);

  // ── Fetch subscription status from backend ─────────────────────────────────
  const fetchSubscription = useCallback(async (token) => {
    try {
      const res = await fetch(`${API_BASE}/subscriptions/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSubscription(data);
        localStorage.setItem('crm_subscription', JSON.stringify(data));
      }
    } catch {
      // Non-fatal — subscription state will be null
    }
  }, []);

  // ── Restore session on load ────────────────────────────────────────────────
  useEffect(() => {
    const storedUser = localStorage.getItem('crm_user');
    const token      = localStorage.getItem('crm_token');
    const storedSub  = localStorage.getItem('crm_subscription');

    if (storedUser && token) {
      setUser(JSON.parse(storedUser));
      if (storedSub) setSubscription(JSON.parse(storedSub));
      // Refresh subscription status from server (detect expiry)
      fetchSubscription(token);
    }
    setLoading(false);
  }, [fetchSubscription]);

  // ── Login ──────────────────────────────────────────────────────────────────
  const login = (userData, token, subscriptionData = null) => {
    setUser(userData);
    localStorage.setItem('crm_user', JSON.stringify(userData));
    localStorage.setItem('crm_token', token);

    if (subscriptionData) {
      setSubscription(subscriptionData);
      localStorage.setItem('crm_subscription', JSON.stringify(subscriptionData));
    } else {
      // Fetch fresh subscription after login
      fetchSubscription(token);
    }
  };

  // ── Logout ─────────────────────────────────────────────────────────────────
  const logout = () => {
    setUser(null);
    setSubscription(null);
    localStorage.removeItem('crm_user');
    localStorage.removeItem('crm_token');
    localStorage.removeItem('crm_subscription');
  };

  // ── Helpers ────────────────────────────────────────────────────────────────
  const isTrialExpired = subscription?.isExpired === true;
  const daysRemaining  = subscription?.daysRemaining ?? null;
  const isTrialActive  = subscription?.status === 'active' && !isTrialExpired;

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      logout,
      role: user?.role,
      subscription,
      isTrialExpired,
      isTrialActive,
      daysRemaining,
      refreshSubscription: () => {
        const token = localStorage.getItem('crm_token');
        if (token) fetchSubscription(token);
      }
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
